import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-sand-100">
      <div
        className="hidden md:block relative grain-bg"
        style={{
          backgroundImage:
            "linear-gradient(rgba(27,67,50,0.55), rgba(27,67,50,0.25)), url(https://images.unsplash.com/photo-1776090974926-0ffca9a8177e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBleHRlcmlvciUyMGRheWxpZ2h0fGVufDB8fHx8MTc3Njk3OTY5M3ww&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <Logo />
          <div className="max-w-md text-white">
            <div className="uppercase tracking-[0.25em] text-xs text-sand-200 mb-4">AI Leasing Agent</div>
            <h1 className="font-heading text-4xl lg:text-5xl font-semibold leading-tight">
              Renewals on autopilot. Past-due handled. Inbox sorted.
            </h1>
            <p className="mt-5 text-sand-100/90 leading-relaxed">
              rentmore24 drafts renewal offers, prioritises emergencies, surfaces past-due balances,
              and researches local rental demand — so you spend less time on email and more time closing leases.
            </p>
          </div>
          <div className="text-xs text-sand-200">© 2026 rentmore24.com</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="md:hidden mb-4"><Logo /></div>
          <div>
            <h2 className="font-heading text-3xl font-semibold">Welcome back</h2>
            <p className="text-sm text-stone-500 mt-1">Sign in to your rentmore24 workspace.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@rentmore24.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-forest-600 hover:bg-forest-500 text-white h-11 rounded-md"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in…" : "Sign in"} <ArrowRight size={18} className="ml-2" />
          </Button>
          <div className="text-sm text-stone-500 text-center">
            New to rentmore24?{" "}
            <Link to="/signup" className="text-forest-600 font-medium hover:underline" data-testid="signup-link">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
